import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Grid,
  TextField,
  Card,
  CardContent,
  Button,
  Snackbar,
  Alert,
  InputAdornment,
  Tooltip,
  CircularProgress,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Chip,
  Divider,
  Avatar,
  Fab,
  Fade,
  Slide,
  Zoom,
  Backdrop,
  Container,
  Stack,
} from "@mui/material";
import {
  Person,
  Call,
  Phone,
  Email,
  CorporateFare,
  BusinessCenter,
  Language,
  LocationOn,
  Edit as EditIcon,
  Save as SaveIcon,
  Delete,
  Add,
  ArrowBack,
  Download,
  Edit,
  Close,
  Folder,
  LocalOffer,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { FaWhatsapp } from "react-icons/fa";
import makeAPIRequest, { makeApiCall } from "../global/apiCall";
import {
  GET,
  GET_CONTACT_BY_ID,
  PUT,
  EDIT_CONTACT,
} from "../global/apiKeys";
import VCard from "vcard-creator";
import GroupsManager from "../components/modern/GroupsManager";
import TagsManager from "../components/modern/TagsManager";
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

const ContactDetails = () => {
  const { isDark } = useCustomTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const contactId = queryParams.get("cardId");
  
  const [fields, setFields] = useState({
    fullName: "",
    orgName: "",
    designation: "",
    phones: [{ phone: "" }],
    emails: [{ email: "" }],
    website: "",
    address: "",
    imgUrl: "",
    groupId: "",
    selectedTags: [],
    notes: "",
    priority: 0,
  });
  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState("");
  const [currentImageView, setCurrentImageView] = useState('front');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [originalFields, setOriginalFields] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    onConfirm: null,
    onDiscard: null
  });
  const [saveButtonRef, setSaveButtonRef] = useState(null);
  const [ddData, setDDData] = useState({});
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]);
  const [groupsDialog, setGroupsDialog] = useState(false);
  const [tagsDialog, setTagsDialog] = useState(false);

  const getContactDetailsById = useCallback(async () => {
    try {
      const res = await makeAPIRequest(GET, GET_CONTACT_BY_ID(contactId));
      const fileData = res?.data?.data;
      if (!fileData) return;
      
      console.log('📞 Contact details received:', {
        id: fileData.id,
        tagIds: fileData.tagIds,
        tags: fileData.tags,
        tagsLength: fileData.tags?.length || 0
      });
      
      // Handle tags - prefer tags array, fallback to tagIds
      let selectedTags = [];
      if (fileData?.tags && Array.isArray(fileData.tags)) {
        selectedTags = fileData.tags;
        console.log('🏷️ Using tags from API:', selectedTags.length);
      } else if (fileData?.tagIds && Array.isArray(fileData.tagIds) && fileData.tagIds.length > 0) {
        console.log('⚠️ Tags not populated, have tagIds:', fileData.tagIds);
        // We'll populate these in the fetchTags function
        selectedTags = [];
      }
      
      let obj = {
        fullName: fileData?.fullName || "",
        orgName: fileData?.orgName || "",
        designation: fileData?.designation || "",
        phones: fileData?.phones || [{ phone: "" }],
        emails: fileData?.emails || [{ email: "" }],
        website: fileData?.website || "",
        address: fileData?.address || "",
        imgUrl: fileData?.imgUrl || "",
        cardImages: fileData?.cardImages || { front: null, back: null },
        hasBothSides: fileData?.hasBothSides || false,
        scanType: fileData?.scanType || 'single',
        groupId: fileData?.groupId || "",
        selectedTags: selectedTags,
        notes: fileData?.notes || "",
        priority: fileData?.priority || 0,
        // Store original tagIds for reference
        _originalTagIds: fileData?.tagIds || [],
      };

      if (fileData?.phones?.length > 0) {
        let phoneTypes = {};
        fileData.phones.forEach((item, index) => {
          phoneTypes[`phone${index}`] = item.type;
        });
        setDDData(prev => ({ ...prev, ...phoneTypes }));
      }

      if (fileData?.emails?.length > 0) {
        let emailTypes = {};
        fileData.emails.forEach((item, index) => {
          emailTypes[`email${index}`] = item.type;
        });
        setDDData(prev => ({ ...prev, ...emailTypes }));
      }

      setFields(obj);
      setOriginalFields(JSON.parse(JSON.stringify(obj)));

      // Set the correct initial image view
      if (obj.cardImages?.front) {
        setCurrentImageView('front');
      } else if (obj.cardImages?.back) {
        setCurrentImageView('back');
      } else if (obj.imgUrl) {
        setCurrentImageView('front'); // For legacy images, treat as front
      }
    } catch (err) {
      console.error("Error fetching contact details:", err);
      setSnackbar({
        open: true,
        message: "Error fetching contact details",
        severity: "error"
      });
    }
  }, [contactId]);

  const fetchGroups = async () => {
    try {
      const response = await makeApiCall('/groups', 'GET');
      if (response.success && Array.isArray(response.data)) {
        setGroups(response.data);
      } else {
        setGroups([]);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await makeApiCall('/tags', 'GET');
      if (response.success && Array.isArray(response.data)) {
        setTags(response.data);
        
        // If selectedTags is empty but we have _originalTagIds, convert them
        if ((!fields.selectedTags || fields.selectedTags.length === 0) && fields._originalTagIds && fields._originalTagIds.length > 0) {
          console.log('🔄 Converting tagIds to tag objects:', fields._originalTagIds);
          const tagObjects = fields._originalTagIds.map(tagId => 
            response.data.find(tag => tag.id === tagId)
          ).filter(Boolean);
          
          console.log('✅ Converted tags:', tagObjects.length);
          setFields(prev => ({ ...prev, selectedTags: tagObjects }));
        }
        
        // Also handle the case where selectedTags contains IDs instead of objects
        else if (fields.selectedTags && Array.isArray(fields.selectedTags) && fields.selectedTags.length > 0) {
          // Check if selectedTags contains IDs (strings) instead of objects
          const firstItem = fields.selectedTags[0];
          if (typeof firstItem === 'string') {
            console.log('🔄 Converting tag ID strings to objects:', fields.selectedTags);
            const tagObjects = fields.selectedTags.map(tagId => 
              response.data.find(tag => tag.id === tagId)
            ).filter(Boolean);
            setFields(prev => ({ ...prev, selectedTags: tagObjects }));
          }
        }
      } else {
        setTags([]);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
    }
  };

  useEffect(() => {
    getContactDetailsById();
    fetchGroups();
    fetchTags();
  }, [getContactDetailsById]);

  const handleDial = (phoneNumber) => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  const handleWhatsApp = (phoneNumber) => {
    if (phoneNumber) {
      const message = `Hi! I am reaching out to you.`;
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
        message
      )}`;
      window.open(whatsappUrl, "_blank");
    }
  };

  const handlePhoneCall = (phone) => {
    if (!phone) {
      setSnackbar({
        open: true,
        message: "No phone number available",
        severity: "error",
      });
      return;
    }
    handleDial(phone.replace(/\D/g, ""));
  };

  const handleWhatsAppClick = (phone) => {
    if (!phone) {
      setSnackbar({
        open: true,
        message: "No phone number available",
        severity: "error",
      });
      return;
    }
    
    // Clean the phone number - remove non-digit characters
    const cleanPhone = phone.replace(/\D/g, "");
    
    // Create a default message with contact information
    const message = `Hi! I got your contact information: ${fields.fullName}${fields.designation ? `, ${fields.designation}` : ''}${fields.orgName ? ` at ${fields.orgName}` : ''}`;
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappUrl, "_blank");
    
    setSnackbar({
      open: true,
      message: "Opening WhatsApp...",
      severity: "info",
    });
  };

  const handleFieldChange = (key, value) => {
    setFields(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const handlePhoneChange = (index, value) => {
    const updatedPhones = [...fields.phones];
    updatedPhones[index] = { phone: value };
    setFields((prev) => ({
      ...prev,
      phones: updatedPhones,
    }));
    setHasUnsavedChanges(true);
  };

  const handleEmailChange = (index, value) => {
    const updatedEmails = [...fields.emails];
    updatedEmails[index] = { email: value };
    setFields((prev) => ({
      ...prev,
      emails: updatedEmails,
    }));
    setHasUnsavedChanges(true);
  };

  const handleAddPhone = () => {
    setFields((prev) => ({
      ...prev,
      phones: [...prev.phones, { phone: "" }],
    }));
    setHasUnsavedChanges(true);
  };

  const handleRemovePhone = (index) => {
    if (fields.phones.length > 1) {
      setFields((prev) => ({
        ...prev,
        phones: prev.phones.filter((_, i) => i !== index),
      }));
    }
    setHasUnsavedChanges(true);
  };

  const handleAddEmail = () => {
    setFields((prev) => ({
      ...prev,
      emails: [...prev.emails, { email: "" }],
    }));
    setHasUnsavedChanges(true);
  };

  const handleRemoveEmail = (index) => {
    if (fields.emails.length > 1) {
      setFields((prev) => ({
        ...prev,
        emails: prev.emails.filter((_, i) => i !== index),
      }));
    }
    setHasUnsavedChanges(true);
  };

  const handleDropDownaData = (type, details) => {
    setDDData({ ...ddData, ...{ [type]: details } });
  };

  const toggleEdit = (key) => {
    setEditingField(editingField === key ? null : key);
    setTempValue(fields[key]);
  };

  const saveEdit = (key) => {
    handleFieldChange(key, tempValue);
    toggleEdit(key);
  };

  const onSavePress = async () => {
    setIsLoading(true);
    try {
      const phonesData = fields.phones
        .filter((phone) => phone.phone.trim() !== "")
        .map((phone, index) => ({
          type: ddData[`phone${index}`] || "Mobile",
          phone: phone.phone,
        }));

      const emailsData = fields.emails
        .filter((email) => email.email.trim() !== "")
        .map((email, index) => ({
          type: ddData[`email${index}`] || "Personal",
          email: email.email,
        }));

      const payload = {
        ...fields,
        phones: phonesData,
        emails: emailsData,
        groupId: fields.groupId || null,
        tagIds: fields.selectedTags.map(tag => tag.id) || [],
        notes: fields.notes || "",
        priority: fields.priority || 0,
      };

      await makeAPIRequest(PUT, EDIT_CONTACT(contactId), payload);
      setSnackbar({
        open: true,
        message: 'Contact updated successfully',
        severity: 'success'
      });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error saving contact:", error);
      setSnackbar({
        open: true,
        message: 'Error updating contact',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactSave = async (contactData) => {
    const supported = "contacts" in navigator;

    if ("contacts" in navigator) {
      try {
        const contact = {
          name: [contactData.fullName],
          tel: Object.keys(contactData)
            .filter((key) => key.startsWith("phone"))
            .map((key) => contactData[key])
            .filter((number) => number), // Collect phone numbers
          email: contactData.emails ? [contactData.emails] : [],
          address: contactData.address ? [contactData.address] : [],
          organization: contactData.orgName ? [contactData.orgName] : [],
        };

        await navigator.contacts.save([contact]);
        setHasUnsavedChanges(false); // Clear unsaved changes flag on successful save
      } catch (error) {
        console.error("Error saving contact:", error);
        downloadVCard(contactData); // Fallback
        setHasUnsavedChanges(false); // Clear flag even for fallback since vCard is downloaded
      }
    } else {
      downloadVCard(contactData); // Fallback
      setHasUnsavedChanges(false); // Clear flag for fallback
    }
  };

  const downloadVCard = (contactData) => {
    const phoneNumbers = Object.keys(contactData)
      .filter((key) => key.startsWith("phone"))
      .map((key) => contactData[key])
      .filter((number) => number);

    const vCard = new VCard();

    // Sequentially add fields
    vCard.addName(
      contactData.fullName.split(" ").slice(-1)[0],
      contactData.fullName.split(" ").slice(0, -1).join(" ")
    );
    if (contactData.orgName) vCard.addCompany(contactData.orgName);
    if (contactData.designation) vCard.addJobtitle(contactData.designation);
    if (contactData.address)
      vCard.addAddress("", "", contactData.address, "", "", "", "");

    // Add all phone numbers
    phoneNumbers.forEach((phone) => {
      vCard.addPhoneNumber(phone, "WORK");
    });

    // Add email and website if available
    if (contactData.emails) vCard.addEmail(contactData.emails);
    if (contactData.website) vCard.addURL(contactData.website);

    // Generate and download the .vcf file
    const blob = new Blob([vCard.toString()], {
      type: "text/vcard;charset=utf-8",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${contactData.fullName.replace(/\s+/g, "_")}.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    alert("vCard downloaded!");
    return;
  };

  const handleDownloadImage = () => {
    const currentImage = fields.cardImages?.[currentImageView] || fields.imgUrl;
    if (currentImage) {
      // Open image in a modal instead of a new tab
      setImageModalOpen(true);

      setSnackbar({
        open: true,
        message: "Image opened. Use your device's save or share options to download.",
        severity: "info",
      });
    } else {
      setSnackbar({
        open: true,
        message: "No image available to view",
        severity: "warning",
      });
    }
  };

  const handleShare = () => {
    if (!fields.fullName && !fields.phones[0]?.phone) {
      setSnackbar({
        open: true,
        message: "No contact information available to share",
        severity: "error",
      });
      return;
    }

    const shareData = {
      title: `Contact: ${fields.fullName || 'Contact'}`,
      text: `
Name: ${fields.fullName || 'N/A'}
${fields.designation ? `Designation: ${fields.designation}` : ''}
${fields.orgName ? `Organization: ${fields.orgName}` : ''}
${fields.phones[0]?.phone ? `Phone: ${fields.phones[0].phone}` : ''}
${fields.emails[0]?.email ? `Email: ${fields.emails[0].email}` : ''}
${fields.website ? `Website: ${fields.website}` : ''}
${fields.address ? `Address: ${fields.address}` : ''}
`.trim()
    };

    if (navigator.share) {
      navigator.share(shareData)
        .then(() => {
          setSnackbar({
            open: true,
            message: "Contact shared successfully!",
            severity: "success"
          });
        })
        .catch((err) => {
          console.error("Error sharing:", err);
          copyToClipboard(shareData.text);
        });
    } else {
      copyToClipboard(shareData.text);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setSnackbar({
          open: true,
          message: "Contact information copied to clipboard!",
          severity: "success"
        });
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
        setSnackbar({
          open: true,
          message: "Failed to copy contact information",
          severity: "error"
        });
      });
  };

  const handleDownloadVCard = async () => {
    // Check if Permissions API is supported
    if ('permissions' in navigator) {
      try {
        console.log('Checking contact permissions...');
        
        // Check current permission status
        const permissionStatus = await navigator.permissions.query({ name: 'contacts' });
        console.log('Permission status:', permissionStatus.state);
        
        if (permissionStatus.state === 'granted') {
          console.log('Contacts permission already granted');
          await saveContactNatively();
          return;
        } else if (permissionStatus.state === 'prompt') {
          // We need to request permission
          console.log('Will prompt for contacts permission');
          
          // Show a message explaining why we need contacts permission
          setSnackbar({
            open: true,
            message: "We'll ask for contacts permission to save this contact directly to your device",
            severity: "info"
          });
          
          // Try to save (this will trigger the permission prompt)
          setTimeout(async () => {
            try {
              await saveContactNatively();
              return;
            } catch (error) {
              console.error('Error after permission prompt:', error);
              // Fall back to vCard
              downloadVCardFile();
            }
          }, 1500);
          return;
        } else {
          // Permission denied, fall back to vCard
          console.log('Contacts permission denied. Using vCard fallback.');
          downloadVCardFile();
          return;
        }
      } catch (error) {
        console.error('Error with permissions API:', error);
        // If permission query fails, try direct method or fallback
        if ('contacts' in navigator && 'save' in navigator.contacts) {
          try {
            await saveContactNatively();
            return;
          } catch (contactError) {
            console.error('Error saving contact directly:', contactError);
            // Fall back to vCard
            downloadVCardFile();
          }
        } else {
          // No permissions API or contacts API support, use vCard
          downloadVCardFile();
        }
      }
    } else {
      // No Permissions API, check if Contacts API is available directly
      if ('contacts' in navigator && 'save' in navigator.contacts) {
        try {
          await saveContactNatively();
          return;
        } catch (error) {
          console.error('Error saving contact directly:', error);
          // Fall back to vCard
          downloadVCardFile();
        }
      } else {
        // No Contacts API support, use vCard
        console.log('No contacts API support. Using vCard method.');
        downloadVCardFile();
      }
    }
  };

  // Function to save contact using the native Contacts API
  const saveContactNatively = async () => {
    if (!('contacts' in navigator) || !('save' in navigator.contacts)) {
      throw new Error('Contacts API not supported');
    }
    
    console.log('Attempting to save contact natively...');
    
    const contactData = {
      name: [fields.fullName],
      tel: Object.keys(fields)
        .filter(key => key.startsWith("phone") && fields[key])
        .map(key => fields[key]),
      email: fields.emails && fields.emails.length > 0 
        ? fields.emails.map(email => email.email)
        : [],
      address: fields.address ? [fields.address] : [],
      organization: fields.orgName ? [fields.orgName] : []
    };
    
    console.log('Contact data prepared:', contactData);
    
    try {
      await navigator.contacts.save(contactData);
      console.log('Contact saved successfully!');
      
      setSnackbar({
        open: true,
        message: "Contact saved to your device!",
        severity: "success"
      });
      
      return true;
    } catch (error) {
      console.error('Error saving contact:', error);
      throw error;
    }
  };

  // Function to generate and download vCard file
  const downloadVCardFile = () => {
    console.log('Using vCard download method');
    
    try {
      // Create vCard data
      const vCardData = generateVCardData();
      console.log('vCard data generated successfully');
      
      // Create downloadable blob
      const blob = new Blob([vCardData], { type: 'text/vcard' });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fields.fullName || 'contact'}.vcf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('vCard download initiated');
      
      // Show instructions modal for how to use the vCard file
      setSnackbar({
        open: true,
        message: "Contact file downloaded. Import this file to your contacts app.",
        severity: "success"
      });
      
      // Show more detailed instructions after a short delay
      setTimeout(() => {
        setSnackbar({
          open: true,
          message: "On Android: Open Contacts app and look for Import option. On iOS: The file should open automatically.",
          severity: "info"
        });
      }, 3000);
      
      return true;
    } catch (error) {
      console.error("Error creating contact:", error);
      
      setSnackbar({
        open: true,
        message: "Failed to create contact file: " + error.message,
        severity: "error"
      });
      
      return false;
    }
  };

  const generateVCardData = () => {
    const vCard = new VCard();
    
    // Add name - try to split into first/last name if possible
    const nameParts = fields.fullName.split(" ");
    if (nameParts.length > 1) {
      const lastName = nameParts.pop();
      const firstName = nameParts.join(" ");
      vCard.addName(lastName, firstName);
    } else {
      vCard.addName(fields.fullName);
    }
    
    // Add company and job title
    if (fields.orgName) vCard.addCompany(fields.orgName);
    if (fields.designation) vCard.addJobtitle(fields.designation);
    
    // Add address if available
    if (fields.address) {
      vCard.addAddress("", "", fields.address, "", "", "", "");
    }
    
    // Add all phone numbers with their types
    fields.phones.forEach((phone, index) => {
      if (phone.phone) {
        const phoneType = ddData[`phone${index}`] || "CELL";
        vCard.addPhoneNumber(phone.phone, phoneType);
      }
    });
    
    // Add all emails with their types
    fields.emails.forEach((email, index) => {
      if (email.email) {
        const emailType = ddData[`email${index}`] || "WORK";
        vCard.addEmail(email.email, emailType);
      }
    });
    
    // Add website if available
    if (fields.website) {
      vCard.addURL(fields.website);
    }
    
    return vCard.toString();
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Navigation blocking when there are unsaved changes
  const handleBeforeNavigate = () => {
    if (hasUnsavedChanges) {
      return new Promise((resolve) => {
        setConfirmDialog({
          open: true,
          onConfirm: () => {
            // Save changes and continue navigation
            onSavePress().then(() => {
              setConfirmDialog({ open: false, onConfirm: null, onDiscard: null });
              resolve(true);
            }).catch(() => {
              // Keep dialog open if save fails
              resolve(false);
            });
          },
          onDiscard: () => {
            // Discard changes and continue navigation
            setHasUnsavedChanges(false);
            setConfirmDialog({ open: false, onConfirm: null, onDiscard: null });
            resolve(true);
          }
        });
        
        // Focus on save button if it exists
        if (saveButtonRef) {
          setTimeout(() => saveButtonRef.focus(), 100);
        }
      });
    }
    return Promise.resolve(true);
  };

  // Note: useBlocker requires data router setup, so we handle navigation protection through other means

  // Handle browser back button and page unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, onConfirm: null, onDiscard: null });
  };

  const renderIcons = (key) => {
    switch (key) {
      case 'fullName':
        return <Person />;
      case 'designation':
        return <BusinessCenter />;
      case 'orgName':
        return <CorporateFare />;
      case 'website':
        return <Language />;
      case 'address':
        return <LocationOn />;
      default:
        return null;
    }
  };

  const dropdownOptions = ["Work", "Personal", "Home", "Mobile", "Whatsapp"];

  return (
    <Container
      maxWidth={false}
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: isDark 
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
        position: "relative",
        p: 0,
      }}
    >
      {/* Header */}
      <Fade in timeout={600}>
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            p: 1.5,
            background: isDark 
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.9) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
            borderBottom: `0.5px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <IconButton
            onClick={() => {
              if (hasUnsavedChanges) {
                handleBeforeNavigate().then((canNavigate) => {
                  if (canNavigate) {
                    navigate(-1);
                  }
                });
              } else {
                navigate(-1);
              }
            }}
            size="small"
            sx={{ 
              color: isDark ? 'white' : 'primary.main', 
              mr: 1.5,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
              '&:hover': {
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)',
                transform: 'scale(1.02)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700,
              background: isDark 
                ? 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%)'
                : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.1)',
            }}
          >
            Contact Details
          </Typography>
        </Paper>
      </Fade>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          pb: 10,
          px: 1.5,
          pt: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Premium Card Image */}
        <Zoom in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: '100%',
              mb: 3,
              borderRadius: '24px',
              display: "flex",
              flexDirection: "column",
              background: isDark
                ? 'linear-gradient(145deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.85) 50%, rgba(51, 65, 85, 0.9) 100%)'
                : 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 50%, rgba(241, 245, 249, 0.85) 100%)',
              backdropFilter: 'blur(20px)',
              border: isDark
                ? '2px solid transparent'
                : '2px solid rgba(255, 255, 255, 0.8)',
              position: 'relative',
              overflow: 'visible',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: isDark
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%)',
                borderRadius: '24px',
                zIndex: 0,
              },
              boxShadow: isDark
                ? '0 20px 40px -12px rgba(0, 0, 0, 0.6), 0 8px 16px -8px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                : '0 20px 40px -12px rgba(0, 0, 0, 0.15), 0 8px 16px -8px rgba(99, 102, 241, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
              '&:hover': {
                transform: 'translateY(-8px) scale(1.02)',
                boxShadow: isDark
                  ? '0 32px 64px -12px rgba(0, 0, 0, 0.7), 0 12px 24px -8px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
                  : '0 32px 64px -12px rgba(0, 0, 0, 0.2), 0 12px 24px -8px rgba(99, 102, 241, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            {/* Business Card Banner */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                minHeight: '300px',
                padding: { xs: 2, sm: 3 },
                background: isDark
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
                marginBottom: 2,
                position: 'relative',
              }}
            >
              {/* Business Card Image */}
              {(() => {
                const imageUrl = fields.cardImages?.[currentImageView] || fields.imgUrl;
                console.log('🖼️ ContactDetails - Current image URL:', imageUrl);
                console.log('🖼️ ContactDetails - Current view:', currentImageView);
                console.log('🖼️ ContactDetails - cardImages:', fields.cardImages);
                console.log('🖼️ ContactDetails - imgUrl:', fields.imgUrl);
                return imageUrl;
              })() ? (
                <img
                  src={fields.cardImages?.[currentImageView] || fields.imgUrl}
                  alt={`Business card - ${currentImageView} side`}
                  onError={(e) => {
                    console.error('🖼️ Image failed to load:', e.target.src);
                    console.error('🖼️ Error details:', e);
                  }}
                  onLoad={(e) => {
                    console.log('🖼️ Image loaded successfully:', e.target.src);
                  }}
                  style={{
                    maxWidth: '400px',
                    width: '90%',
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    position: 'relative',
                    zIndex: 3,
                    display: 'block',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: 'white',
                    textAlign: 'center'
                  }}
                >
                  <BusinessCenter sx={{ fontSize: 80, mb: 2, opacity: 0.7 }} />
                  <Typography variant="h6" sx={{ opacity: 0.8 }}>
                    No Business Card Image
                  </Typography>
                </Box>
              )}

              {/* Image Toggle Buttons - Floating on banner */}
              {fields.hasBothSides && fields.cardImages?.front && fields.cardImages?.back && (
                <Box sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 1,
                  zIndex: 2
                }}>
                  <Button
                    size="medium"
                    variant={currentImageView === 'front' ? 'contained' : 'outlined'}
                    onClick={() => setCurrentImageView('front')}
                    sx={{
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      bgcolor: currentImageView === 'front' ? 'success.main' : 'rgba(255, 255, 255, 0.9)',
                      color: currentImageView === 'front' ? 'white' : 'success.main',
                      borderColor: currentImageView === 'front' ? 'success.main' : 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        bgcolor: currentImageView === 'front' ? 'success.dark' : 'rgba(255, 255, 255, 1)',
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                  >
                    📄 Front Side
                  </Button>
                  <Button
                    size="medium"
                    variant={currentImageView === 'back' ? 'contained' : 'outlined'}
                    onClick={() => setCurrentImageView('back')}
                    sx={{
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      bgcolor: currentImageView === 'back' ? 'warning.main' : 'rgba(255, 255, 255, 0.9)',
                      color: currentImageView === 'back' ? 'white' : 'warning.main',
                      borderColor: currentImageView === 'back' ? 'warning.main' : 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        bgcolor: currentImageView === 'back' ? 'warning.dark' : 'rgba(255, 255, 255, 1)',
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                  >
                    📄 Back Side
                  </Button>
                </Box>
              )}
            </Box>

            {/* Profile Info Section */}
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: isDark ? '#ffffff' : '#1a1a1a',
                  fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' }
                }}
              >
                {fields.fullName || "No Name"}
              </Typography>

              {fields.designation && (
                <Typography
                  variant="h6"
                  sx={{
                    color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
                    mb: 0.5,
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.125rem' }
                  }}
                >
                  {fields.designation}
                </Typography>
              )}

              {fields.orgName && (
                <Typography
                  variant="h6"
                  sx={{
                    color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {fields.orgName}
                </Typography>
              )}


              {/* Download Button - Floating on banner */}
              {(fields.cardImages?.[currentImageView] || fields.imgUrl) && (
                <Fab
                  size="medium"
                  onClick={handleDownloadImage}
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    background: 'rgba(255, 255, 255, 0.9)',
                    color: 'primary.main',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 1)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Download />
                </Fab>
              )}
            </Box>
          </Paper>
        </Zoom>


        {/* Contact Info Cards */}
        <Stack spacing={1} sx={{ width: "100%", maxWidth: 500 }}>
          {/* Phone cards */}
          {fields.phones.map((phone, index) => (
            <Slide key={`phone-${index}`} in timeout={600 + index * 100} direction="up">
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 0.6,
                  background: isDark 
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.02) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%)',
                  backdropFilter: 'blur(8px)',
                  border: `0.5px solid ${isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)'}`,
                  borderRadius: 2.5,
                  boxShadow: isDark 
                    ? '0 6px 10px -2px rgba(0, 0, 0, 0.25), 0 2px 4px -1px rgba(0, 0, 0, 0.15)'
                    : '0 6px 10px -2px rgba(0, 0, 0, 0.06), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: isDark 
                      ? '0 12px 16px -3px rgba(0, 0, 0, 0.3), 0 6px 6px -3px rgba(0, 0, 0, 0.2)'
                      : '0 12px 16px -3px rgba(0, 0, 0, 0.08), 0 6px 6px -3px rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <Box sx={{ 
                  color: 'primary.main', 
                  mr: 1.5,
                  p: 0.6,
                  borderRadius: 1.5,
                  background: isDark 
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(37, 99, 235, 0.12) 100%)'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(37, 99, 235, 0.08) 100%)',
                }}>
                  <Phone />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <input
                    type="tel"
                    placeholder="Enter phone number"
                    value={phone.phone || ''}
                    onChange={(e) => handlePhoneChange(index, e.target.value)}
                    style={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: '18px',
                      fontWeight: 600,
                      color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#000',
                      padding: '8px 0 8px 10px',
                      fontFamily: 'inherit',
                    }}
                  />
                </Box>
                {/* Action buttons */}
                <Stack direction="row" spacing={1}>
                  <Tooltip title="WhatsApp">
                    <IconButton 
                      onClick={() => {
                        const cleanPhone = phone.phone.replace(/\D/g, "");
                        if (cleanPhone) {
                          const whatsappUrl = `https://wa.me/${cleanPhone}`;
                          window.open(whatsappUrl, "_blank");
                        } else {
                          setSnackbar({
                            open: true,
                            message: "Please enter a valid phone number",
                            severity: "warning",
                          });
                        }
                      }}
                      size="small"
                      sx={{ 
                        color: "#25D366",
                        background: 'rgba(37, 211, 102, 0.1)',
                        '&:hover': {
                          background: 'rgba(37, 211, 102, 0.2)',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <FaWhatsapp size="18" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Call">
                    <IconButton 
                      onClick={() => {
                        const cleanPhone = phone.phone.replace(/\D/g, "");
                        if (cleanPhone) {
                          window.location.href = `tel:${cleanPhone}`;
                        } else {
                          setSnackbar({
                            open: true,
                            message: "Please enter a valid phone number",
                            severity: "warning",
                          });
                        }
                      }}
                      size="small"
                      sx={{ 
                        color: 'primary.main',
                        background: 'rgba(59, 130, 246, 0.1)',
                        '&:hover': {
                          background: 'rgba(59, 130, 246, 0.2)',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <Call fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {fields.phones.length > 1 && (
                    <Tooltip title="Remove">
                      <IconButton 
                        onClick={() => handleRemovePhone(index)}
                        size="small"
                        sx={{ 
                          color: "error.main",
                          background: 'rgba(239, 68, 68, 0.1)',
                          '&:hover': {
                            background: 'rgba(239, 68, 68, 0.2)',
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Paper>
            </Slide>
          ))}

          {/* Add Phone button */}
          <Fade in timeout={800}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                startIcon={<Add />}
                onClick={handleAddPhone}
                variant="outlined"
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  fontSize: "14px",
                  fontWeight: 600,
                  background: isDark 
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%)',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    background: isDark 
                      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: isDark 
                      ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                      : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Add Phone
              </Button>
            </Box>
          </Fade>

          {/* Name card */}
          <Slide in timeout={700} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.06)',
                },
              }}
            >
              <Box sx={{ 
                color: 'primary.main', 
                mr: 1,
                p: 0.5,
                borderRadius: 2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
              }}>
                <Person />
              </Box>
              <Box sx={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder="Full Name"
                  value={fields.fullName}
                  onChange={(e) => handleFieldChange("fullName", e.target.value)}
                  style={{
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#000',
                    padding: '8px 0 8px 10px',
                    fontFamily: 'inherit',
                  }}
                />
              </Box>
            </Paper>
          </Slide>

          {/* Organization card */}
          <Slide in timeout={800} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.06)',
                },
              }}
            >
              <Box sx={{ 
                color: 'primary.main', 
                mr: 1,
                p: 0.5,
                borderRadius: 2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
              }}>
                <CorporateFare />
              </Box>
              <Box sx={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder="Organization"
                  value={fields.orgName || ''}
                  onChange={(e) => handleFieldChange("orgName", e.target.value)}
                  style={{
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#000',
                    padding: '8px 0 8px 10px',
                    fontFamily: 'inherit',
                  }}
                />
              </Box>
            </Paper>
          </Slide>

          {/* Designation card */}
          <Slide in timeout={900} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.06)',
                },
              }}
            >
              <Box sx={{ 
                color: 'primary.main', 
                mr: 1,
                p: 0.5,
                borderRadius: 2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
              }}>
                <BusinessCenter />
              </Box>
              <Box sx={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder="Job Title / Designation"
                  value={fields.designation || ''}
                  onChange={(e) => handleFieldChange("designation", e.target.value)}
                  style={{
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#000',
                    padding: '8px 0 8px 10px',
                    fontFamily: 'inherit',
                  }}
                />
              </Box>
            </Paper>
          </Slide>

          {/* Email cards */}
          {fields.emails.map((email, index) => (
            <Slide key={`email-${index}`} in timeout={1000 + index * 100} direction="up">
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 0.6,
                  background: isDark 
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.02) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%)',
                  backdropFilter: 'blur(8px)',
                  border: `0.5px solid ${isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)'}`,
                  borderRadius: 2.5,
                  boxShadow: isDark 
                    ? '0 6px 10px -2px rgba(0, 0, 0, 0.25), 0 2px 4px -1px rgba(0, 0, 0, 0.15)'
                    : '0 6px 10px -2px rgba(0, 0, 0, 0.06), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: isDark 
                      ? '0 12px 16px -3px rgba(0, 0, 0, 0.3), 0 6px 6px -3px rgba(0, 0, 0, 0.2)'
                      : '0 12px 16px -3px rgba(0, 0, 0, 0.08), 0 6px 6px -3px rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <Box sx={{ 
                  color: 'primary.main', 
                  mr: 1.5,
                  p: 0.6,
                  borderRadius: 1.5,
                  background: isDark 
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(37, 99, 235, 0.12) 100%)'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(37, 99, 235, 0.08) 100%)',
                }}>
                  <Email />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={email.email || ''}
                    onChange={(e) => handleEmailChange(index, e.target.value)}
                    style={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#000',
                      padding: '8px 0 8px 10px',
                      fontFamily: 'inherit',
                    }}
                  />
                </Box>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Send Email">
                    <IconButton 
                      onClick={() => {
                        if (email.email) {
                          window.location.href = `mailto:${email.email}`;
                        } else {
                          setSnackbar({
                            open: true,
                            message: "Please enter a valid email address",
                            severity: "warning",
                          });
                        }
                      }}
                      size="small"
                      sx={{ 
                        color: 'primary.main',
                        background: 'rgba(59, 130, 246, 0.1)',
                        '&:hover': {
                          background: 'rgba(59, 130, 246, 0.2)',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <Email fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {fields.emails.length > 1 && (
                    <Tooltip title="Remove">
                      <IconButton 
                        onClick={() => handleRemoveEmail(index)}
                        size="small"
                        sx={{ 
                          color: "error.main",
                          background: 'rgba(239, 68, 68, 0.1)',
                          '&:hover': {
                            background: 'rgba(239, 68, 68, 0.2)',
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Paper>
            </Slide>
          ))}

          {/* Add Email button */}
          <Fade in timeout={1200}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                startIcon={<Add />}
                onClick={handleAddEmail}
                variant="outlined"
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  fontSize: "14px",
                  fontWeight: 600,
                  background: isDark 
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%)',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    background: isDark 
                      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: isDark 
                      ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                      : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Add Email
              </Button>
            </Box>
          </Fade>

          {/* Group Selection */}
          <Slide in timeout={1300} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.06)',
                },
              }}
            >
              <Box sx={{ 
                color: 'primary.main', 
                mr: 1,
                p: 0.5,
                borderRadius: 2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
              }}>
                <Folder />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth variant="standard">
                  <InputLabel 
                    sx={{
                      color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                      fontSize: "14px",
                    }}
                  >
                    Group
                  </InputLabel>
                  <Select
                    value={fields.groupId || ''}
                    onChange={(e) => handleFieldChange("groupId", e.target.value)}
                    disableUnderline
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'text.primary',
                      '& .MuiSelect-icon': {
                        color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>No Group</em>
                    </MenuItem>
                    {Array.isArray(groups) && groups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: group.color,
                            }}
                          />
                          {group.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Button
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => setGroupsDialog(true)}
                sx={{ 
                  minWidth: 'auto',
                  fontSize: 12,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  color: 'primary.main',
                  background: isDark 
                    ? 'rgba(59, 130, 246, 0.1)'
                    : 'rgba(59, 130, 246, 0.05)',
                  '&:hover': {
                    background: isDark 
                      ? 'rgba(59, 130, 246, 0.2)'
                      : 'rgba(59, 130, 246, 0.1)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Manage
              </Button>
            </Paper>
          </Slide>

          {/* Tags Selection */}
          <Slide in timeout={1400} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                p: 1.2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                minHeight: 64,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.06)',
                },
              }}
            >
              <Box sx={{ 
                color: 'primary.main', 
                mr: 1,
                p: 0.5,
                borderRadius: 2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                mt: 0.5,
              }}>
                <LocalOffer />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  multiple
                  options={tags}
                  value={fields.selectedTags || []}
                  onChange={(event, newValue) => {
                    handleFieldChange("selectedTags", newValue);
                  }}
                  getOptionLabel={(option) => option.name}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                        size="small"
                        sx={{
                          bgcolor: option.color + '20',
                          color: option.color,
                          border: `1px solid ${option.color}40`,
                          fontWeight: 600,
                          '& .MuiChip-deleteIcon': {
                            color: option.color,
                            '&:hover': {
                              color: option.color,
                              opacity: 0.8,
                            },
                          },
                        }}
                      />
                    ))
                  }
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <LocalOffer sx={{ color: option.color, mr: 1 }} />
                      {option.name}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Select Tags"
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontSize: "16px",
                          fontWeight: 600,
                          color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'text.primary',
                          padding: "4px 0",
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.6)',
                          opacity: 1,
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Button
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => setTagsDialog(true)}
                sx={{ 
                  minWidth: 'auto',
                  fontSize: 12,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  color: 'primary.main',
                  background: isDark 
                    ? 'rgba(59, 130, 246, 0.1)'
                    : 'rgba(59, 130, 246, 0.05)',
                  mt: 0.5,
                  '&:hover': {
                    background: isDark 
                      ? 'rgba(59, 130, 246, 0.2)'
                      : 'rgba(59, 130, 246, 0.1)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Manage
              </Button>
            </Paper>
          </Slide>

          {/* Priority */}
          <Slide in timeout={1500} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.06)',
                },
              }}
            >
              <Box sx={{ 
                color: fields.priority > 0 ? 'warning.main' : 'primary.main', 
                mr: 1,
                p: 0.5,
                borderRadius: 2,
                background: fields.priority > 0 
                  ? (isDark 
                      ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.15) 100%)'
                      : 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)')
                  : (isDark 
                      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)'
                      : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)'),
              }}>
                {fields.priority > 0 ? <BusinessCenter /> : <BusinessCenter />}
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth variant="standard">
                  <InputLabel 
                    sx={{
                      color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                      fontSize: "14px",
                    }}
                  >
                    Priority
                  </InputLabel>
                  <Select
                    value={fields.priority || 0}
                    onChange={(e) => handleFieldChange("priority", e.target.value)}
                    disableUnderline
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'text.primary',
                      '& .MuiSelect-icon': {
                        color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                      },
                    }}
                  >
                    <MenuItem value={-1} sx={{ color: 'text.disabled' }}>Low</MenuItem>
                    <MenuItem value={0} sx={{ color: 'success.main' }}>Normal</MenuItem>
                    <MenuItem value={1} sx={{ color: 'warning.main' }}>High</MenuItem>
                    <MenuItem value={2} sx={{ color: 'error.main' }}>Very High</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Paper>
          </Slide>

          {/* Notes */}
          <Slide in timeout={1600} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                p: 1.2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                minHeight: 80,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.06)',
                },
              }}
            >
              <Box sx={{ 
                color: 'primary.main', 
                mr: 1,
                p: 0.5,
                borderRadius: 2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                mt: 0.5,
              }}>
                <Edit />
              </Box>
              <Box sx={{ flex: 1 }}>
                <textarea
                  placeholder="Add notes..."
                  rows={2}
                  value={fields.notes || ""}
                  onChange={(e) => handleFieldChange("notes", e.target.value)}
                  style={{
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#000',
                    padding: '8px 0 8px 10px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
              </Box>
            </Paper>
          </Slide>

          {/* Other fields (website, address) */}
          {Object.entries(fields)
            .filter(([key]) => !["phones", "emails", "imgUrl", "fullName", "orgName", "designation", "groupId", "selectedTags", "notes", "priority", "_originalTagIds", "cardImages", "hasBothSides", "scanType"].includes(key) && key !== "")
            .map(([key, value], index) => (
              <Slide key={key} in timeout={1700 + index * 100} direction="up">
                <Paper
                  elevation={0}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.2,
                    background: isDark 
                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                    borderRadius: 3,
                    boxShadow: isDark 
                      ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                      : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark 
                        ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                        : '0 20px 25px -5px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.06)',
                    },
                  }}
                >
                  <Box sx={{ 
                    color: 'primary.main', 
                    mr: 1,
                    p: 1.2,
                    borderRadius: 2,
                    background: isDark 
                      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)'
                      : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                  }}>
                    {renderIcons(key)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <input
                      type={key === 'website' ? 'url' : 'text'}
                      placeholder={key === 'website' ? 'Website URL' : key === 'address' ? 'Address' : key}
                      value={value || ''}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      style={{
                        width: '100%',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        fontSize: '16px',
                        fontWeight: 600,
                        color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#000',
                        padding: '8px 0 8px 10px',
                        fontFamily: 'inherit',
                      }}
                    />
                  </Box>
                </Paper>
              </Slide>
            ))}
        </Stack>

        {/* Save Changes Button */}
        <Zoom in timeout={2000}>
          <Box sx={{ width: "100%", textAlign: "center", mt: 4, mb: 10 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={onSavePress}
              disabled={isLoading}
              sx={{
                borderRadius: 4,
                px: 6,
                py: 2,
                background: isDark 
                  ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                  : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                fontSize: '16px',
                fontWeight: 600,
                minWidth: 200,
                boxShadow: isDark 
                  ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                  : '0 20px 25px -5px rgba(59, 130, 246, 0.4), 0 10px 10px -5px rgba(59, 130, 246, 0.2)',
                '&:hover': {
                  background: isDark 
                    ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                    : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 25px 30px -5px rgba(0, 0, 0, 0.5), 0 15px 15px -5px rgba(0, 0, 0, 0.4)'
                    : '0 25px 30px -5px rgba(59, 130, 246, 0.5), 0 15px 15px -5px rgba(59, 130, 246, 0.3)',
                },
                '&:disabled': {
                  background: isDark 
                    ? 'rgba(148, 163, 184, 0.3)'
                    : 'rgba(148, 163, 184, 0.5)',
                  color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.7)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Zoom>

        {/* Bottom Action Buttons */}
        <Slide in timeout={2200} direction="up">
          <Paper
            elevation={0}
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              padding: "20px 16px",
              background: isDark 
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              zIndex: 10,
              boxShadow: isDark 
                ? '0 -10px 15px -3px rgba(0, 0, 0, 0.4), 0 -4px 6px -2px rgba(0, 0, 0, 0.3)'
                : '0 -10px 15px -3px rgba(0, 0, 0, 0.1), 0 -4px 6px -2px rgba(0, 0, 0, 0.05)'
            }}
          >
            <Tooltip title="Call">
              <Fab
                size="medium"
                onClick={() => handlePhoneCall(fields.phones[0]?.phone)}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  boxShadow: isDark 
                    ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                    : '0 10px 15px -3px rgba(59, 130, 246, 0.3), 0 4px 6px -2px rgba(59, 130, 246, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    transform: 'scale(1.1) translateY(-2px)',
                    boxShadow: isDark 
                      ? '0 15px 20px -3px rgba(0, 0, 0, 0.4), 0 8px 12px -2px rgba(0, 0, 0, 0.3)'
                      : '0 15px 20px -3px rgba(59, 130, 246, 0.4), 0 8px 12px -2px rgba(59, 130, 246, 0.3)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Phone />
              </Fab>
            </Tooltip>

            <Tooltip title="WhatsApp">
              <Fab
                size="medium"
                onClick={() => handleWhatsAppClick(fields.phones[0]?.phone)}
                sx={{
                  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                  color: 'white',
                  boxShadow: isDark 
                    ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                    : '0 10px 15px -3px rgba(37, 211, 102, 0.3), 0 4px 6px -2px rgba(37, 211, 102, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
                    transform: 'scale(1.1) translateY(-2px)',
                    boxShadow: isDark 
                      ? '0 15px 20px -3px rgba(0, 0, 0, 0.4), 0 8px 12px -2px rgba(0, 0, 0, 0.3)'
                      : '0 15px 20px -3px rgba(37, 211, 102, 0.4), 0 8px 12px -2px rgba(37, 211, 102, 0.3)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <FaWhatsapp size={20} />
              </Fab>
            </Tooltip>

            <Tooltip title="Email">
              <Fab
                size="medium"
                onClick={() => {
                  if (fields.emails[0]?.email) {
                    window.location.href = `mailto:${fields.emails[0].email}`;
                  } else {
                    setSnackbar({
                      open: true,
                      message: "No email address available",
                      severity: "error",
                    });
                  }
                }}
                sx={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  boxShadow: isDark 
                    ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                    : '0 10px 15px -3px rgba(245, 158, 11, 0.3), 0 4px 6px -2px rgba(245, 158, 11, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                    transform: 'scale(1.1) translateY(-2px)',
                    boxShadow: isDark 
                      ? '0 15px 20px -3px rgba(0, 0, 0, 0.4), 0 8px 12px -2px rgba(0, 0, 0, 0.3)'
                      : '0 15px 20px -3px rgba(245, 158, 11, 0.4), 0 8px 12px -2px rgba(245, 158, 11, 0.3)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Email />
              </Fab>
            </Tooltip>

            <Tooltip title="Share">
              <Fab
                size="medium"
                onClick={handleShare}
                sx={{
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  color: 'white',
                  boxShadow: isDark 
                    ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                    : '0 10px 15px -3px rgba(139, 92, 246, 0.3), 0 4px 6px -2px rgba(139, 92, 246, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                    transform: 'scale(1.1) translateY(-2px)',
                    boxShadow: isDark 
                      ? '0 15px 20px -3px rgba(0, 0, 0, 0.4), 0 8px 12px -2px rgba(0, 0, 0, 0.3)'
                      : '0 15px 20px -3px rgba(139, 92, 246, 0.4), 0 8px 12px -2px rgba(139, 92, 246, 0.3)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z"/></svg>
              </Fab>
            </Tooltip>

            <Tooltip title="Save Contact">
              <Fab
                size="medium"
                onClick={handleDownloadVCard}
                sx={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  boxShadow: isDark 
                    ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                    : '0 10px 15px -3px rgba(16, 185, 129, 0.3), 0 4px 6px -2px rgba(16, 185, 129, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    transform: 'scale(1.1) translateY(-2px)',
                    boxShadow: isDark 
                      ? '0 15px 20px -3px rgba(0, 0, 0, 0.4), 0 8px 12px -2px rgba(0, 0, 0, 0.3)'
                      : '0 15px 20px -3px rgba(16, 185, 129, 0.4), 0 8px 12px -2px rgba(16, 185, 129, 0.3)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Download />
              </Fab>
            </Tooltip>
          </Paper>
        </Slide>

        {/* Image Modal Dialog */}
        <Dialog
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {fields.fullName || "Contact"} - Business Card
            <IconButton
              aria-label="close"
              onClick={() => setImageModalOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', p: 1 }}>
            {(fields.cardImages?.[currentImageView] || fields.imgUrl) && (
              <Box>
                <Box
                  component="img"
                  src={fields.cardImages?.[currentImageView] || fields.imgUrl}
                  alt={`${fields.fullName || "Business card"} - ${currentImageView} side`}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                  }}
                />
                {/* Image Toggle Buttons in Modal */}
                {fields.hasBothSides && fields.cardImages?.front && fields.cardImages?.back && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                    <Button
                      size="small"
                      variant={currentImageView === 'front' ? 'contained' : 'outlined'}
                      onClick={() => setCurrentImageView('front')}
                      sx={{
                        bgcolor: currentImageView === 'front' ? 'success.main' : 'transparent',
                        color: currentImageView === 'front' ? 'white' : 'success.main',
                        borderColor: 'success.main',
                      }}
                    >
                      📄 Front Side
                    </Button>
                    <Button
                      size="small"
                      variant={currentImageView === 'back' ? 'contained' : 'outlined'}
                      onClick={() => setCurrentImageView('back')}
                      sx={{
                        bgcolor: currentImageView === 'back' ? 'warning.main' : 'transparent',
                        color: currentImageView === 'back' ? 'white' : 'warning.main',
                        borderColor: 'warning.main',
                      }}
                    >
                      📄 Back Side
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                // Try to trigger device download or share
                if (navigator.share && navigator.canShare) {
                  try {
                    // Try to use Web Share API if available
                    fetch(fields.cardImages?.[currentImageView] || fields.imgUrl)
                      .then(res => res.blob())
                      .then(blob => {
                        const file = new File([blob], `${fields.fullName || 'contact'}_business_card_${fields.hasBothSides ? currentImageView : 'image'}.jpg`, {
                          type: 'image/jpeg'
                        });
                        
                        if (navigator.canShare({ files: [file] })) {
                          navigator.share({
                            title: `${fields.fullName || 'Contact'} Business Card`,
                            files: [file]
                          })
                          .catch(err => {
                            console.error('Share error:', err);
                          });
                        } else {
                          // Fallback for devices that don't support file sharing
                          navigator.share({
                            title: `${fields.fullName || 'Contact'} Business Card`,
                            text: 'Business card image',
                            url: fields.imgUrl
                          })
                          .catch(err => {
                            console.error('Share error:', err);
                          });
                        }
                      })
                      .catch(err => {
                        console.error('Fetch error:', err);
                        // Show snackbar with instructions
                        setSnackbar({
                          open: true,
                          message: "Long-press on the image to save it to your device",
                          severity: "info",
                        });
                      });
                  } catch (error) {
                    console.error('Share setup error:', error);
                    // Show snackbar with instructions
                    setSnackbar({
                      open: true,
                      message: "Long-press on the image to save it to your device",
                      severity: "info",
                    });
                  }
                } else {
                  // Show snackbar with instructions for iOS/Android
                  setSnackbar({
                    open: true,
                    message: "Long-press on the image to save it to your device",
                    severity: "info",
                  });
                }
              }}
              startIcon={<Download />}
              variant="contained"
              color="primary"
            >
              Save/Share
            </Button>
            <Button
              onClick={() => setImageModalOpen(false)}
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Groups Manager Dialog */}
        <GroupsManager
          open={groupsDialog}
          onClose={() => {
            setGroupsDialog(false);
            fetchGroups(); // Refresh groups after closing
          }}
          onGroupSelect={(group) => {
            handleFieldChange("groupId", group.id);
            setGroupsDialog(false);
          }}
          selectedGroup={groups.find(g => g.id === fields.groupId)}
        />

        {/* Tags Manager Dialog */}
        <TagsManager
          open={tagsDialog}
          onClose={() => {
            setTagsDialog(false);
            fetchTags(); // Refresh tags after closing
          }}
          onTagSelect={(selectedTags) => {
            handleFieldChange("selectedTags", selectedTags);
          }}
          selectedTags={fields.selectedTags}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Confirmation Dialog for Unsaved Changes */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleCloseConfirmDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Unsaved Changes
          </DialogTitle>
          <DialogContent>
            <Typography>
              You have unsaved changes. What would you like to do?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={() => {
                if (confirmDialog.onDiscard) {
                  confirmDialog.onDiscard();
                }
              }}
              color="error"
              variant="outlined"
            >
              Discard Changes
            </Button>
            <Button 
              ref={setSaveButtonRef}
              onClick={() => {
                if (confirmDialog.onConfirm) {
                  confirmDialog.onConfirm();
                }
              }}
              color="primary" 
              variant="contained"
              autoFocus
            >
              Save & Continue
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ContactDetails;
